import * as React from 'react'
import Providers from './Providers'
import Widget, {
  type PreviewOpeningQuestionId,
  type WidgetProps,
} from './Widget'
import { Api } from './api'
import { type RequestsCtx, type SurveyOptions } from './RequestsCtx'
import { flushSync } from 'react-dom'

export type RootProps = {
  onDidMount?: () => void
  onDidClose?: () => void
} & RequestsCtx &
  Pick<
    WidgetProps,
    | 'onHeightChange'
    | 'onConfig'
    | 'onFirstClick'
    | 'onInputFocus'
    | 'onInputBlur'
  >

type State =
  | {
      type: 'idle'
    }
  | ({
      type: 'survey'
      surveyId: string
      openingQuestionId?: PreviewOpeningQuestionId
      autoFocus?: boolean
    } & SurveyOptions)

const Root = React.forwardRef(
  (
    {
      onDidMount,
      onDidClose,
      surveyRespondMutationFn,
      surveyQueryFn,
      ...widgetProps
    }: RootProps,
    ref
  ) => {
    const [opened, setOpened] = React.useState(false)
    const [enableInitialLoading, setEnableInitialLoading] =
      React.useState(false)
    const [incValue, setIncValue] = React.useState(0)
    const [state, setState] = React.useState<State>(() => ({
      type: 'idle',
    }))

    React.useImperativeHandle(
      ref,
      (): Api => ({
        open: (surveyId, { showInitialLoading, ...surveyMeta }) => {
          flushSync(() => {
            setOpened(true)
            setState({
              type: 'survey',
              surveyId,
              ...surveyMeta,
            })
            setEnableInitialLoading(!!showInitialLoading)
            setIncValue(val => val + 1)
          })
        },
        show: () => {
          setOpened(true)
        },
        close: () => {
          setOpened(false)
          onDidClose?.()
        },
        reset: () => {
          setIncValue(val => val + 1)
        },
      }),
      []
    )

    React.useEffect(() => {
      onDidMount?.()
    }, [])

    if (!opened || state.type === 'idle') return null

    return (
      <Providers
        surveyRespondMutationFn={surveyRespondMutationFn}
        surveyQueryFn={surveyQueryFn}
        key={state.surveyId + '$' + incValue}>
        <Widget
          initLoading={enableInitialLoading}
          setOpened={willOpen => {
            const api = (ref as React.RefObject<Api>)?.current
            willOpen ? api?.show() : api?.close()
          }}
          surveyId={state.surveyId}
          meta={state}
          openingQuestionId={state.openingQuestionId}
          autoFocus={state.autoFocus}
          {...widgetProps}
        />
      </Providers>
    )
  }
)

export default Root
